import { StoryblokRichtext, render } from "storyblok-rich-text-react-renderer";
import { FC, ReactNode } from "react";

interface StoryblokRichTextRendererProps {
  document: StoryblokRichtext | undefined | null;
}

// Enhanced renderer for Storyblok Rich Text with better typography
export const StoryblokRichTextRenderer: FC<StoryblokRichTextRendererProps> = ({
  document,
}) => {
  if (!document) {
    return null;
  }

  return render(document, {
    markResolvers: {
      // Mark resolvers for inline text formatting
      link: (children: ReactNode, props: any) => {
        const { linktype, href, target } = props;
        const url = linktype === "email" ? `mailto:${href}` : href;
        return (
          <a
            href={url}
            target={target || "_blank"}
            rel="noopener noreferrer"
            className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 transition-colors duration-200 underline"
          >
            {children}
          </a>
        );
      },
      bold: (children: ReactNode) => (
        <strong className="font-semibold">{children}</strong>
      ),
      italic: (children: ReactNode) => <em className="italic">{children}</em>,
      underline: (children: ReactNode) => (
        <span className="underline">{children}</span>
      ),
      code: (children: ReactNode) => (
        <code className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-sm font-mono">
          {children}
        </code>
      ),
      // Add other marks as needed
    },
    nodeResolvers: {
      // Node resolvers for block-level elements with improved styling
      paragraph: (children: ReactNode) => (
        <p className="mb-4 text-secondary leading-relaxed">
          {children}
        </p>
      ),
      heading: (children: ReactNode, props: any) => {
        const { level } = props;
        switch (level) {
          case 1:
            return (
              <h1 className="text-2xl font-bold text-text mb-4">
                {children}
              </h1>
            );
          case 2:
            return (
              <h2 className="text-xl font-semibold text-text mb-3">
                {children}
              </h2>
            );
          case 3:
            return (
              <h3 className="text-lg font-medium text-text mb-3">
                {children}
              </h3>
            );
          case 4:
            return (
              <h4 className="text-base font-medium text-text mb-2">
                {children}
              </h4>
            );
          case 5:
            return (
              <h5 className="text-sm font-medium text-text mb-2">
                {children}
              </h5>
            );
          case 6:
            return (
              <h6 className="text-xs font-medium text-text mb-2">
                {children}
              </h6>
            );
          default:
            return (
              <h2 className="text-xl font-semibold text-text mb-3">
                {children}
              </h2>
            );
        }
      },
      bullet_list: (children: ReactNode) => (
        <ul className="list-disc list-outside mb-4 pl-5 text-secondary space-y-1.5">
          {children}
        </ul>
      ),
      ordered_list: (children: ReactNode) => (
        <ol className="list-decimal list-outside mb-4 pl-5 text-secondary space-y-1.5">
          {children}
        </ol>
      ),
      list_item: (children: ReactNode) => <li className="mb-1">{children}</li>,
      image: (props: any) => {
        if (!props) return null;
        const { src, alt, title } = props || {};
        if (!src) return null;

        return (
          <figure className="my-4">
            <img
              src={src}
              alt={alt || ""}
              className="rounded-lg w-full h-auto shadow-sm"
            />
            {title && (
              <figcaption className="mt-2 text-sm text-center text-muted">
                {title}
              </figcaption>
            )}
          </figure>
        );
      },
      blockquote: (children: ReactNode) => (
        <blockquote className="pl-4 border-l-4 border-purple-400 dark:border-purple-600 italic my-4 text-muted">
          {children}
        </blockquote>
      ),
      // hr resolver removed due to type constraint
      // Add other node types as needed
    },
    // You can add blockResolvers here if needed for custom Storyblok components
  });
};
